import { useCallback, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Typography, Space } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRealtimeStore } from '../store';

dayjs.extend(utc);

const { Text } = Typography;

// Local SLA lookups by client and triage category (minutes)
// Defaults will be used when a client-specific SLA is not defined.
const SLA_RULES = {
  default: {
    1: 5,
    2: 15,
    3: 120,
    4: 120,
    5: 120,
    default: 120,
  },
  URGHosp: {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
  'NSW Ambulance': {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
  'Ambulance Victoria': {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
};

const EXCLUDED_STATUSES = ['completed', 'cancelled', 'canceled'];

const parseCat = (trcat) => {
  if (!trcat) return null;
  const matches = String(trcat).match(/\d+/g);
  if (!matches || matches.length === 0) return null;
  const nums = matches.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
  return nums.length ? Math.min(...nums) : null;
};

const getSlaMinutes = (client, trcat) => {
  const rules = SLA_RULES[client] || SLA_RULES.default;
  if (Number.isFinite(trcat)) {
    return rules[trcat] ?? rules.default ?? null;
  }
  return rules.default ?? null;
};

const deriveArrivalMoment = (row) => {
  const raw =
    row.firstappearinwaiting ||
    row.appointcreatedatetime ||
    row.appointmentdatetime ||
    row.finalschedate ||
    row.appointmentdate;
  if (!raw) return null;
  const parsed = dayjs(raw).utc();
  return parsed.isValid() ? parsed : null;
};

const deriveWaitMinutes = (row) => {
  const explicit =
    row.waittime ??
    row.waitTime ??
    (Number.isFinite(row.wait) ? row.wait : null) ??
    null;

  if (Number.isFinite(explicit)) return Number(explicit);

  const anchor =
    row.firstappearinwaiting ||
    row.appointcreatedatetime ||
    row.appointmentdatetime;
  if (!anchor) return null;
  const diff = dayjs().diff(dayjs(anchor), 'minute');
  return Number.isFinite(diff) ? diff : null;
};

const deriveElapsed = (row) => {
  const explicit =
    row['Elapsed time'] ??
    row.elapsedTime ??
    row.elapsed ??
    row.elapsedMinutes ??
    null;
  if (Number.isFinite(explicit)) return explicit;
  const start = row.appointcreatedatetime || row.appointmentdatetime;
  if (!start) return null;
  const end =
    row.completedatetime ||
    row.billingdatetime ||
    row.withdoctordatetime ||
    dayjs().toISOString();
  const diff = dayjs(end).diff(dayjs(start), 'minute');
  return Number.isFinite(diff) ? diff : null;
};

const getWaitColor = (waitMinutes, slaMinutes) => {
  if (
    !Number.isFinite(waitMinutes) ||
    !Number.isFinite(slaMinutes) ||
    slaMinutes <= 0
  ) {
    return { color: '#595959', label: 'No SLA' };
  }
  const ratio = waitMinutes / slaMinutes;
  if (ratio >= 0.9) return { color: '#ff4d4f', label: '90%+' };
  if (ratio >= 0.7) return { color: '#fa8c16', label: '70%+' };
  if (ratio >= 0.5) return { color: '#fadb14', label: '50%+' };
  return { color: '#52c41a', label: '<50%' };
};

const RealtimeWaitingRoom = () => {
  const {
    realtimeAppointments,
    isFetchingRealtime,
    fetchRealtimeAppointments,
  } = useRealtimeStore((state) => ({
    realtimeAppointments: state.realtimeAppointments,
    isFetchingRealtime: state.isFetchingRealtime,
    fetchRealtimeAppointments: state.fetchRealtimeAppointments,
  }));

  useEffect(() => {
    fetchRealtimeAppointments();
    const id = setInterval(() => {
      // Placeholder: once API is live this will refresh data every minute
      fetchRealtimeAppointments();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [fetchRealtimeAppointments]);

  const tableData = useMemo(() => {
    return (realtimeAppointments || []).map((row) => {
      const trcatRaw = row.trCAT || row.trcat || row.trCat || row.TrCat;
      const trcatNum = parseCat(trcatRaw);
      const slaMinutes = getSlaMinutes(row.name, trcatNum);
      const waitMinutes = deriveWaitMinutes(row);
      const waitMeta = getWaitColor(waitMinutes, slaMinutes);
      const arrivalMoment = deriveArrivalMoment(row);

      return {
        key:
          row.appointmentguid ||
          row.finalclient ||
          row.finaldoctor ||
          `${row.name}-${row.appointmentdate || Math.random()}`,
        arrivalTime: arrivalMoment ? arrivalMoment.format('h:mm A') : '-',
        arrivalTs: arrivalMoment ? arrivalMoment.valueOf() : null,
        client: row.name || '-',
        sitecode: row.fnote || row.sitecode || row.SiteCode || '-',
        trcat: trcatRaw || '-',
        trcatNum,
        slaMinutes,
        waitMinutes,
        waitColor: waitMeta.color,
        clockStatus: row['Clock status'] || row.clockStatus || row.clockstatus,
        stopReason: row['Stop Reason'] || row.stopReason || '-',
        apptStatus:
          row['Appt status'] ||
          row.apptStatus ||
          row.status ||
          row.finalstatus ||
          '-',
        elapsed: deriveElapsed(row),
      };
    });
  }, [realtimeAppointments]);

  const buildFilters = useCallback(
    (dataIndex) => {
      const values = new Set(
        tableData
          .map((row) => row[dataIndex])
          .filter((v) => typeof v === 'string' && v.trim().length > 0)
      );
      return Array.from(values).map((value) => ({
        text: value,
        value,
      }));
    },
    [tableData]
  );

  const clientFilters = useMemo(() => buildFilters('client'), [buildFilters]);
  const siteFilters = useMemo(() => buildFilters('sitecode'), [buildFilters]);
  const clockFilters = useMemo(
    () => buildFilters('clockStatus'),
    [buildFilters]
  );
  const reasonFilters = useMemo(
    () => buildFilters('stopReason'),
    [buildFilters]
  );
  const statusFilters = useMemo(
    () => buildFilters('apptStatus'),
    [buildFilters]
  );

  const defaultStatusFilteredValue = useMemo(() => {
    return statusFilters
      .map((f) => f.value)
      .filter((v) => !EXCLUDED_STATUSES.includes(v.toLowerCase()));
  }, [statusFilters]);

  const columns = useMemo(
    () => [
      {
        title: 'Arrival Time',
        dataIndex: 'arrivalTime',
        key: 'arrivalTime',
        width: 120,
        sorter: (a, b) => (a.arrivalTs || 0) - (b.arrivalTs || 0),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Client',
        dataIndex: 'client',
        key: 'client',
        width: 160,
        ellipsis: true,
        filters: clientFilters,
        filterSearch: true,
        onFilter: (value, record) =>
          (record.client || '').toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: 'SiteCode',
        dataIndex: 'sitecode',
        key: 'sitecode',
        width: 150,
        ellipsis: true,
        filters: siteFilters,
        filterSearch: true,
        onFilter: (value, record) =>
          (record.sitecode || '').toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: 'trCAT',
        dataIndex: 'trcat',
        key: 'trcat',
        width: 90,
        sorter: (a, b) =>
          Number.isFinite(a.trcatNum) && Number.isFinite(b.trcatNum)
            ? a.trcatNum - b.trcatNum
            : (a.trcat || '').localeCompare(b.trcat || ''),
      },
      {
        title: 'Wait Time (min)',
        dataIndex: 'waitMinutes',
        key: 'waitMinutes',
        width: 130,
        sorter: (a, b) => (a.waitMinutes || 0) - (b.waitMinutes || 0),
        render: (val, record) => (
          <Tag
            style={{
              backgroundColor: record.waitColor,
              borderColor: record.waitColor,
              color: '#000',
              minWidth: 64,
              textAlign: 'center',
            }}
          >
            {Number.isFinite(val) ? `${val}` : '-'}
          </Tag>
        ),
      },
      {
        title: 'SLA (min)',
        dataIndex: 'slaMinutes',
        key: 'slaMinutes',
        width: 110,
        render: (val) => <Text>{Number.isFinite(val) ? `${val}` : '-'}</Text>,
        sorter: (a, b) =>
          (Number.isFinite(a.slaMinutes) ? a.slaMinutes : Infinity) -
          (Number.isFinite(b.slaMinutes) ? b.slaMinutes : Infinity),
      },
      {
        title: 'Clock status',
        dataIndex: 'clockStatus',
        key: 'clockStatus',
        width: 140,
        ellipsis: true,
        render: (val) => <Text>{val || '-'}</Text>,
        filters: clockFilters,
        onFilter: (value, record) =>
          (record.clockStatus || '')
            .toLowerCase()
            .includes(value.toLowerCase()),
      },
      {
        title: 'Stop Reason',
        dataIndex: 'stopReason',
        key: 'stopReason',
        width: 220,
        ellipsis: true,
        render: (val) => <Text>{val || '-'}</Text>,
        filters: reasonFilters,
        onFilter: (value, record) =>
          (record.stopReason || '').toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: 'Elapsed time (min)',
        dataIndex: 'elapsed',
        key: 'elapsed',
        width: 150,
        sorter: (a, b) => (a.elapsed || 0) - (b.elapsed || 0),
        render: (val) => <Text>{Number.isFinite(val) ? val : '-'}</Text>,
      },
      {
        title: 'Appt status',
        dataIndex: 'apptStatus',
        key: 'apptStatus',
        width: 140,
        filters: statusFilters,
        defaultFilteredValue: defaultStatusFilteredValue,
        onFilter: (value, record) =>
          (record.apptStatus || '').toLowerCase().includes(value.toLowerCase()),
        render: (val) => <Text>{val || '-'}</Text>,
      },
    ],
    [
      clientFilters,
      siteFilters,
      clockFilters,
      reasonFilters,
      statusFilters,
      defaultStatusFilteredValue,
    ]
  );

  return (
    <Card
      title={
        <Space size="middle">
          <Text strong style={{ fontSize: 16 }}>
            Waiting Room
          </Text>
        </Space>
      }
      style={{
        width: '100%',
        marginBottom: 24,
        boxShadow: 'none',
        border: '1px solid #f0f0f0',
      }}
      bodyStyle={{ padding: 8 }}
      bordered={false}
    >
      <Table
        size="middle"
        dataSource={tableData}
        columns={columns}
        pagination={false}
        loading={isFetchingRealtime}
        tableLayout="fixed"
        scroll={{ y: 280, x: true }}
        rowKey="key"
      />
    </Card>
  );
};

export default RealtimeWaitingRoom;
